import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageTitle } from '../components/PageTitle';
import { Plus, Video, Trash2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VideoManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [videos, setVideos] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    category: 'tutoriales'
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleCreate = (e) => {
    e.preventDefault();
    const newVideo = {
      id: Date.now(),
      ...formData,
      created_at: new Date().toISOString()
    };
    setVideos([newVideo, ...videos]);
    toast.success('Vídeo añadido correctamente');
    setShowForm(false);
    setFormData({ title: '', url: '', description: '', category: 'tutoriales' });
  };

  const handleDelete = (id) => {
    if (!window.confirm('¿Eliminar este vídeo?')) return;
    setVideos(videos.filter(v => v.id !== id));
    toast.success('Vídeo eliminado');
  };

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-gray-950 p-6">
      <div className="max-w-6xl mx-auto">
        <PageTitle>Añadir Vídeos</PageTitle>

        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-red-500 text-white px-6 py-3 font-bold uppercase flex items-center gap-2 border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition mb-8"
        >
          <Plus size={20} />
          Nuevo Vídeo
        </button>

        {showForm && (
          <div className="bg-gray-900 border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">Añadir Nuevo Vídeo</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-white font-bold mb-2">Título</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full p-3 border-4 border-black"
                  placeholder="Ej: Tutorial completo E-Home"
                />
              </div>
              <div>
                <label className="block text-white font-bold mb-2">URL del Vídeo (YouTube, Vimeo, etc.)</label>
                <input
                  type="url"
                  required
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  className="w-full p-3 border-4 border-black"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              <div>
                <label className="block text-white font-bold mb-2">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full p-3 border-4 border-black"
                  rows="3"
                  placeholder="Describe el contenido del vídeo..."
                ></textarea>
              </div>
              <div>
                <label className="block text-white font-bold mb-2">Categoría</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full p-3 border-4 border-black"
                >
                  <option value="tutoriales">Tutoriales</option>
                  <option value="propiedades">Propiedades</option>
                  <option value="testimonios">Testimonios</option>
                  <option value="consejos">Consejos</option>
                </select>
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-red-500 text-white p-3 font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                >
                  Añadir Vídeo
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 bg-gray-200 text-black p-3 font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid gap-6">
          {videos.length === 0 ? (
            <div className="bg-gray-900 border-4 border-black p-8 text-center">
              <Video size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No hay vídeos añadidos aún</p>
            </div>
          ) : (
            videos.map(video => (
              <div key={video.id} className="bg-gray-900 border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{video.title}</h3>
                    <p className="text-gray-400 text-sm mb-2">{video.description}</p>
                    <span className="inline-block bg-red-500 text-white px-3 py-1 text-xs font-bold">
                      {video.category.toUpperCase()}
                    </span>
                    <div className="mt-3">
                      <a
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 flex items-center gap-2 text-sm"
                      >
                        <ExternalLink size={16} />
                        Ver vídeo
                      </a>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(video.id)}
                    className="text-red-500 hover:text-red-400 ml-4"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};